import { Time } from '@df/common';
import { z } from 'zod';

const bookYearDtoSchema = z.object({ start: z.string(), end: z.string() });
// String branch first: WASM row output and most wire values carry the canonical
// compact string, so the common case must not pay a failed DTO branch.
const bookYearSchema = Time.BookYear.schema.or(
  bookYearDtoSchema.transform((obj, ctx): Time.BookYear => {
    const bookYear = Time.BookYear.fromDto(obj);
    if (bookYear.isErr()) {
      ctx.addIssue({ code: 'custom', message: bookYear.error });
      return z.NEVER;
    }
    return bookYear.value;
  }),
);

/**
 * Book years in trusted WASM output are already canonical strings; cast
 * without validation (ADR-0011 trust boundary). Use only on WASM results.
 */
const trustedBookYearSchema = z
  .string()
  .transform((str): Time.BookYear => Time.BookYear.unsafeFromString(str));

const yearMonthSchema = Time.YearMonth.schema;

const yearMonthSpanSchema = z
  .object({
    first: yearMonthSchema,
    last: yearMonthSchema,
  })
  .transform((arg, ctx): Time.YearMonthSpan => {
    const result = Time.YearMonthSpan.fromYearMonthDto(arg);

    if (result.isErr()) {
      ctx.addIssue({ code: 'custom', message: `Invalid YearMonthSpan string: ${result}` });
      return z.NEVER;
    }

    return result.value;
  });

// String branch first: WASM row output and most wire values carry a canonical
// date string, so the common case brands without a Date allocation. Non-string
// or non-canonical inputs fall back to Date coercion.
const dateStringSchema = z.union([
  Time.NaiveDate.schema,
  z.coerce
    .date()
    .transform((date: Date): Time.NaiveDate =>
      Time.NaiveDate.unsafeFromString(date.toLocaleDateString('sv')),
    ),
]);

const dateSpanSchema = z
  .object({
    first: dateStringSchema,
    last: dateStringSchema,
  })
  .transform((arg, ctx) => {
    const result = Time.NaiveDateSpan.fromDateSpanDto(arg);

    if (result.isErr()) {
      ctx.addIssue({
        code: 'custom',
        message: `Invalid NaiveDateSpan dto: ${JSON.stringify(arg)}`,
      });
      return z.NEVER;
    }

    return result.value;
  });

const periodSchema = bookYearSchema.or(yearMonthSchema);

export {
  bookYearSchema,
  dateSpanSchema,
  dateStringSchema,
  periodSchema,
  trustedBookYearSchema,
  yearMonthSchema,
  yearMonthSpanSchema,
};
